import { localKey } from '@/components/molecules/Table';
import { fetchRequestWithToken } from '@/config/axios';
import { SOCKET_EVENT_NAMES } from '@/constants/socket.events';
import { notify } from '@/helpers/index';
import { customFieldStore } from '@/state/CustomFieldStore';
import { SocketRequest } from '@/config/socket';
import { ContactCustomField, CustomField } from '@/types/customField.types';
import { SocketResponse } from '@/types/socketResponse.type';
import { VisibilityState } from '@tanstack/react-table';
import { AxiosResponse } from 'axios';
import { useState } from 'react';

export type CreateCustomFieldPayload = {
  field: string;
  type: string;
  metaData: Array<string> | null;
};

export type UpdateCustomFieldPayload = {
  contactId: string;
  customFieldId: string;
  value: string | null;
};

type UseCustomFieldOptions = {
  resetOptionFields: () => void;
};

export const useCustomField = (customFieldOptions?: UseCustomFieldOptions) => {
  const [open, setOpen] = useState(false);
  const [customFields, setCustomFields] = useState<CustomField[]>(
    () => customFieldStore?.customFields ?? [],
  );
  const [customFieldsListData, setCustomFieldsListData] = useState([]);
  const [totalCustomFields, setTotalCustomFields] = useState(0);
  const [listLoader, setListLoader] = useState(false);

  const closeModal = () => {
    setOpen(false);
  };
  const openModal = () => {
    setOpen(true);
  };

  const createCustomField = (data: CreateCustomFieldPayload, opt?: any) => {
    customFieldStore.setLoading(true);
    fetchRequestWithToken({
      url: `/custom-fields`,
      method: 'POST',
      data,
    })
      .then(({ data }) => {
        opt?.resetForm({ values: '' });
        customFieldOptions?.resetOptionFields();
        customFieldStore.setCustomFields([
          data,
          ...customFieldStore.customFields,
        ]);

        const fromLs = localStorage.getItem(localKey);
        const oldColumnVisibility = fromLs
          ? (JSON.parse(fromLs) as unknown as VisibilityState)
          : {};
        oldColumnVisibility[data?.field] = false;

        localStorage.setItem(localKey, JSON.stringify(oldColumnVisibility));

        notify('success', 'Custom field created successfully');
      })
      .catch(() => {
        notify('error', 'Something went wrong');
      })
      .finally(() => {
        customFieldStore.setLoading(false);
        closeModal();
      });
  };

  const deleteCustomField = (id: string) => {
    fetchRequestWithToken({
      url: `/custom-fields/${id}`,
      method: 'DELETE',
    })
      .then(() => {
        customFieldStore.setCustomFields(
          customFieldStore?.customFields?.filter(
            customField => customField?._id !== id,
          ),
        );
        notify('success', 'Custom field deleted successfully');
      })
      .catch(() => {
        notify('error', 'Something went wrong');
      });
  };

  const updateCustomField = (id: string, data: CreateCustomFieldPayload) => {
    customFieldStore.setLoading(true);
    fetchRequestWithToken({
      url: `/custom-fields/${id}`,
      method: 'PUT',
      data: {
        field: data?.field,
      },
    })
      .then((data: AxiosResponse<CustomField>) => {
        setCustomFields(prev => {
          return prev?.map(cf => {
            if (cf?._id === id) {
              return data?.data;
            } else {
              return cf;
            }
          });
        });

        const preparedData = customFieldStore?.customFields?.map(
          customField => {
            if (customField?._id === id) {
              return data?.data;
            }
            return customField;
          },
        );
        customFieldStore.setCustomFields(preparedData);

        notify('success', 'Custom field updated successfully');
        closeModal();
      })
      .catch(() => {
        notify('error', 'Something went wrong');
      })
      .finally(() => {
        customFieldStore.setLoading(false);
      });
  };

  const getCustomFields = () => {
    customFieldStore.setLoading(true);
    fetchRequestWithToken({
      url: '/custom-fields',
      method: 'GET',
    })
      .then(({ data }) => {
        customFieldStore.setCustomFields(data);
        setCustomFields(data);
        setCustomFieldObjects(data);
      })
      .catch(() => {
        notify('error', 'Something went wrong');
      });
    customFieldStore.setLoading(false);
  };

  const getCustomFieldsWithFilter = (
    filter?: string,
    showMainLoader = false,
  ) => {
    if (showMainLoader) {
      customFieldStore.setLoading(true);
    } else {
      setListLoader(true);
    }
    fetchRequestWithToken({
      url: '/custom-fields' + (filter ? `?${filter}` : ''),
      method: 'GET',
      params: {
        isFilter: true,
      },
    })
      .then(({ data }) => {
        setCustomFieldsListData(data.data);
        setTotalCustomFields(data?.meta?.totalCount);
      })
      .catch(() => {
        notify('error', 'Something went wrong');
      })
      .finally(() => {
        customFieldStore.setLoading(false);
        setListLoader(false);
      });
  };

  const setCustomFieldObjects = (customFields: CustomField[]) => {
    customFields?.map((customField: CustomField) => {
      customFieldStore.setCustomFieldsObject(customField?.field, null);
    });
  };

  const updateContactWithCustomField = async (
    data: UpdateCustomFieldPayload,
  ) => {
    try {
      customFieldStore.setLoading(true);
      const response: SocketResponse<ContactCustomField> = await SocketRequest(
        SOCKET_EVENT_NAMES.UPDATE_CONTACT_CUSTOM_FIELD_VALUE,
        { data },
      );

      if (response.error) {
        notify('error', response?.error as string);
      } else if (response?.data) {
        notify('success', 'Custom field updated successfully');
      }
    } catch (error) {
      notify('error', 'Something went wrong');
    } finally {
      customFieldStore.setLoading(false);
    }
  };

  const getContactCustomFields = async (contactId: string) => {
    try {
      const response: SocketResponse<ContactCustomField[]> =
        await SocketRequest(SOCKET_EVENT_NAMES.GET_CUSTOM_FIELD_BY_CONTACT_ID, {
          data: {
            contactId,
          },
        });

      if (response.error) {
        notify('error', response?.error as string);
      } else if (response?.data) {
        const customFields = response?.data;
        customFieldStore.resetCustomFieldsObject();
        customFields?.map(customField => {
          customFieldStore.setCustomFieldsObject(
            customField?.custom_field_id,
            customField?.value,
          );
        });
      }
    } catch (error) {
      notify('error', 'Something went wrong');
    }
  };

  return {
    open,
    customFields,
    customFieldsListData,
    totalCustomFields,
    listLoader,
    closeModal,
    openModal,
    setCustomFields,
    createCustomField,
    updateContactWithCustomField,
    getContactCustomFields,
    getCustomFields,
    getCustomFieldsWithFilter,
    deleteCustomField,
    updateCustomField,
  };
};
